const PAYMENT_DRAFT_STORAGE_KEY = "monobankPaymentDraft";

const LEGACY_PAYMENT_STORAGE_KEYS = [
  "monobankPaymentIdempotencyKey",
  "monobankInvoiceId",
  "monobankPaymentUrl",
  "monobankPaymentAmount",
  "monobankOrderSnapshot",
  "monobankOrderSnapshotHash",
  "monobankPaymentCreatedAt",
  "monobankPaymentExpiresAt",
];

const PENDING_PAYMENT_STORAGE_KEYS = [
  "monobankPendingOrderId",
  "monobankPendingOrderNumber",
];

const getSessionStorage = () => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const normalizeString = (value) => String(value ?? "").trim();

const normalizeMoney = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Number(numberValue.toFixed(2)) : 0;
};

const normalizeQuantity = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getItemSortKey = (item) =>
  [
    item.productId,
    item.productCode,
    item.weight,
    item.name,
    item.price,
    item.quantity,
  ]
    .map(normalizeString)
    .join("|");

export const buildMonobankOrderSnapshot = (orderPayload = {}) => {
  const cartItems = Array.isArray(orderPayload.cartItems)
    ? orderPayload.cartItems
    : [];

  const normalizedItems = cartItems
    .map((item) => ({
      productId: normalizeString(item.productId),
      productCode: normalizeString(item.productCode),
      name: normalizeString(item.name),
      manufacturer: normalizeString(item.manufacturer),
      weight: normalizeString(item.weight),
      price: normalizeMoney(item.price),
      quantity: normalizeQuantity(item.quantity),
      averageCostPrice: normalizeMoney(item.averageCostPrice),
    }))
    .sort((a, b) => getItemSortKey(a).localeCompare(getItemSortKey(b)));

  return {
    customerName: normalizeString(orderPayload.customerName),
    phone: normalizeString(orderPayload.phone),
    email: normalizeString(orderPayload.email).toLowerCase(),
    deliveryMethod: normalizeString(orderPayload.deliveryMethod),
    city: normalizeString(orderPayload.city),
    branchNumber: normalizeString(orderPayload.branchNumber),
    novaPostCityRef: normalizeString(orderPayload.novaPostCityRef),
    novaPostBranchRef: normalizeString(orderPayload.novaPostBranchRef),
    novaPostBranchNumber: normalizeString(orderPayload.novaPostBranchNumber),
    novaPostPointType: normalizeString(orderPayload.novaPostPointType),
    novaPoshtaDelivery: orderPayload.novaPoshtaDelivery || null,
    street: normalizeString(orderPayload.street),
    buildingNumber: normalizeString(orderPayload.buildingNumber),
    postalCode: normalizeString(orderPayload.postalCode),
    apartmentNumber: normalizeString(orderPayload.apartmentNumber),
    entranceNumber: normalizeString(orderPayload.entranceNumber),
    floor: normalizeString(orderPayload.floor),
    courierNote: normalizeString(orderPayload.courierNote),
    paymentMethod: normalizeString(orderPayload.paymentMethod),
    totalAmount: normalizeMoney(orderPayload.totalAmount),
    bonusKopecksToRedeem: normalizeQuantity(orderPayload.bonusKopecksToRedeem),
    cartItems: normalizedItems,
  };
};

const stableStringify = (value) => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

export const getMonobankOrderSnapshotHash = (orderPayload = {}) => {
  const snapshotSource = stableStringify(
    buildMonobankOrderSnapshot(orderPayload)
  );
  let hash = 2166136261;

  for (let index = 0; index < snapshotSource.length; index += 1) {
    hash ^= snapshotSource.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `${(hash >>> 0).toString(16).padStart(8, "0")}-${
    snapshotSource.length
  }`;
};

const createPaymentIdempotencyKey = (orderSnapshotHash) => {
  const randomPart =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return `mono-${orderSnapshotHash}-${randomPart}`;
};

const isPaymentDraftExpired = (draft = {}) => {
  if (!draft.paymentExpiresAt) return false;

  const expiresAt = new Date(draft.paymentExpiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
};

export const getMonobankPaymentDraft = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const rawDraft = storage.getItem(PAYMENT_DRAFT_STORAGE_KEY);
    return rawDraft ? JSON.parse(rawDraft) : null;
  } catch {
    return null;
  }
};

export const getMonobankPendingOrder = () => {
  const storage = getSessionStorage();
  if (!storage) return { orderId: "", orderNumber: "" };

  const draft = getMonobankPaymentDraft();

  return {
    orderId:
      normalizeString(draft?.orderId) ||
      normalizeString(storage.getItem("monobankPendingOrderId")),
    orderNumber:
      normalizeString(draft?.orderNumber) ||
      normalizeString(storage.getItem("monobankPendingOrderNumber")),
  };
};

export const clearMonobankPaymentDraft = ({
  includePendingOrder = true,
} = {}) => {
  const storage = getSessionStorage();
  if (!storage) return;

  storage.removeItem(PAYMENT_DRAFT_STORAGE_KEY);
  LEGACY_PAYMENT_STORAGE_KEYS.forEach((key) => storage.removeItem(key));

  if (includePendingOrder) {
    PENDING_PAYMENT_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
  }
};

export const saveMonobankPaymentDraft = (draft = {}) => {
  const storage = getSessionStorage();
  if (!storage) return draft;

  const normalizedDraft = {
    ...draft,
    paymentAmount: normalizeMoney(draft.paymentAmount),
    paymentCreatedAt: draft.paymentCreatedAt || new Date().toISOString(),
    paymentExpiresAt: draft.paymentExpiresAt || "",
  };

  storage.setItem(
    PAYMENT_DRAFT_STORAGE_KEY,
    JSON.stringify(normalizedDraft)
  );

  LEGACY_PAYMENT_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
  storage.setItem(
    "monobankPaymentIdempotencyKey",
    normalizedDraft.idempotencyKey || ""
  );
  storage.setItem(
    "monobankOrderSnapshotHash",
    normalizedDraft.orderSnapshotHash || ""
  );
  storage.setItem(
    "monobankPaymentAmount",
    String(normalizedDraft.paymentAmount)
  );
  storage.setItem(
    "monobankPaymentCreatedAt",
    normalizedDraft.paymentCreatedAt
  );
  storage.setItem(
    "monobankPaymentExpiresAt",
    normalizedDraft.paymentExpiresAt
  );

  if (normalizedDraft.orderSnapshot) {
    storage.setItem(
      "monobankOrderSnapshot",
      JSON.stringify(normalizedDraft.orderSnapshot)
    );
  }

  if (normalizedDraft.invoiceId) {
    storage.setItem("monobankInvoiceId", normalizedDraft.invoiceId);
  }

  if (normalizedDraft.paymentUrl) {
    storage.setItem("monobankPaymentUrl", normalizedDraft.paymentUrl);
  }

  if (normalizedDraft.orderId) {
    storage.setItem("monobankPendingOrderId", normalizedDraft.orderId);
  }

  if (normalizedDraft.orderNumber) {
    storage.setItem(
      "monobankPendingOrderNumber",
      String(normalizedDraft.orderNumber)
    );
  }

  return normalizedDraft;
};

export const getOrCreateMonobankPaymentDraft = (orderPayload = {}) => {
  const orderSnapshot = buildMonobankOrderSnapshot(orderPayload);
  const orderSnapshotHash = getMonobankOrderSnapshotHash(orderPayload);
  const paymentAmount = normalizeMoney(orderPayload.totalAmount);
  const existingDraft = getMonobankPaymentDraft();
  const pendingOrder = getMonobankPendingOrder();

  if (
    existingDraft?.idempotencyKey &&
    existingDraft.orderSnapshotHash === orderSnapshotHash &&
    normalizeMoney(existingDraft.paymentAmount) === paymentAmount &&
    !isPaymentDraftExpired(existingDraft)
  ) {
    return {
      ...existingDraft,
      orderSnapshot,
      orderSnapshotHash,
      paymentAmount,
      isReused: true,
    };
  }

  return saveMonobankPaymentDraft({
    idempotencyKey: createPaymentIdempotencyKey(orderSnapshotHash),
    ...pendingOrder,
    orderSnapshot,
    orderSnapshotHash,
    paymentAmount,
    paymentCreatedAt: new Date().toISOString(),
  });
};
